import React, { useRef, useState } from 'react'
import { CompatibleLink } from 'src/components/CompatibleLink/CompatibleLink'
import { useDeletePortfolioTagMutation, useEditPortfolioTagMutation } from 'src/graphql/manual/portfolioCustomTagHooks'

import { RemoveTagConfirmModal } from './RemoveTagConfirmModal'

export interface CustomTagItem {
  id: string
  name: string
  slug?: string | null
}

interface Props {
  tags: CustomTagItem[]
  solutionId: number
  solutionDocumentId: string
  onRefresh: (removedTagId?: string) => void
  showActions?: boolean
}

export const CustomTagList: React.FC<Props> = ({
  tags,
  solutionId,
  solutionDocumentId,
  onRefresh,
  showActions = false,
}) => {
  const [removeModal, setRemoveModal] = useState<{ tagId: string; tagName: string } | null>(null)
  const [editingTagId, setEditingTagId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const [editPortfolioTag] = useEditPortfolioTagMutation()
  const [deletePortfolioTag] = useDeletePortfolioTagMutation()

  const handleEditSubmit = async (tagId: string) => {
    if (!editValue.trim()) return
    try {
      await editPortfolioTag({ variables: { tagDocumentId: tagId, name: editValue.trim() } })
      setEditingTagId(null)
      onRefresh()
    } catch (err: any) {
      alert(err?.message || 'Failed to edit tag')
    }
  }

  const handleDelete = async (tagId: string) => {
    await deletePortfolioTag({ variables: { tagDocumentId: tagId } })
    setActiveMenuId(null)
    onRefresh()
  }

  if (!tags || tags.length === 0) return null

  return (
    <>
      {tags.map((tag) => (
        <div key={tag.id} className="relative flex items-center">
          {editingTagId === tag.id ? (
            <div className="flex items-center gap-1 mt-1.5 mr-4">
              <input
                autoFocus
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleEditSubmit(tag.id)
                  if (e.key === 'Escape') setEditingTagId(null)
                }}
                className="border border-navy rounded-[12px] px-2 py-0.5 text-[12px] outline-none w-[120px]"
              />
              <button
                type="button"
                onClick={() => handleEditSubmit(tag.id)}
                className="text-[11px] text-navy font-medium"
              >
                Save
              </button>
            </div>
          ) : (
            <span className="py-[5px] px-[14px] gap-[8px] flex bg-[#D6E8FA] text-[#011D58] text-[0.8125rem] font-primary font-medium items-center text-center rounded-[15px] mt-1.5 mr-4 min-h-[30px] overflow-hidden">
              <CompatibleLink
                href={`/custom-tag/${tag.id}/`}
                className="flex items-center h-full text-[#011D58] no-underline hover:no-underline"
              >
                {tag.name}
              </CompatibleLink>
              <button
                type="button"
                onClick={() => setRemoveModal({ tagId: tag.id, tagName: tag.name })}
                className="text-[#011D58] leading-none text-[1rem] font-bold shrink-0"
                title="Remove tag from solution"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M9.33333 0.94L8.39333 0L4.66667 3.72667L0.94 0L0 0.94L3.72667 4.66667L0 8.39333L0.94 9.33333L4.66667 5.60667L8.39333 9.33333L9.33333 8.39333L5.60667 4.66667L9.33333 0.94Z"
                    fill="#011D58"
                  />
                </svg>
              </button>
              {showActions && (
                <div className="relative ml-1" ref={menuRef}>
                  <button
                    type="button"
                    onClick={() => setActiveMenuId(activeMenuId === tag.id ? null : tag.id)}
                    className="text-[#011D58] hover:opacity-70 leading-none text-[14px]"
                    title="Tag options"
                  >
                    ⋮
                  </button>
                  {activeMenuId === tag.id && (
                    <div className="absolute top-full right-0 mt-1 bg-white border border-[#ddd] rounded-[8px] shadow-md z-20 w-[110px]">
                      <div
                        className="px-3 py-2 text-[13px] text-textdark cursor-pointer hover:bg-[#F5F5F5]"
                        onClick={() => {
                          setEditingTagId(tag.id)
                          setEditValue(tag.name)
                          setActiveMenuId(null)
                        }}
                      >
                        Edit
                      </div>
                      <div
                        className="px-3 py-2 text-[13px] text-red-500 cursor-pointer hover:bg-[#F5F5F5]"
                        onClick={() => handleDelete(tag.id)}
                      >
                        Delete
                      </div>
                    </div>
                  )}
                </div>
              )}
            </span>
          )}
        </div>
      ))}

      {removeModal && (
        <RemoveTagConfirmModal
          isOpen={!!removeModal}
          tagId={removeModal.tagId}
          tagName={removeModal.tagName}
          solutionId={solutionId}
          solutionDocumentId={solutionDocumentId}
          onClose={() => setRemoveModal(null)}
          onSuccess={(removedTagId) => {
            onRefresh(removedTagId)
          }}
        />
      )}
    </>
  )
}
